import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Chip,
} from '@mui/material';
import {
  MoreVert as MoreIcon,
  Group as GroupIcon,
  VolumeOff as MuteIcon,
  VolumeUp as UnmuteIcon,
  Archive as ArchiveIcon,
  ArrowBack as BackIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useMessaging } from '../../contexts/MessagingContext';
import { useAuth } from '../../contexts/AuthContext';
import { UserService } from '../../services/userService';
import type { Conversation } from '../../types/message';

interface ConversationHeaderProps {
  conversation: Conversation;
  onBack?: () => void;
}

/**
 * ConversationHeader Component
 *
 * Top bar of conversation view with:
 * - Back button (mobile)
 * - Conversation name and status
 * - Actions menu (mute, archive, info)
 */
export const ConversationHeader: React.FC<ConversationHeaderProps> = ({
  conversation,
  onBack,
}) => {
  const { authState } = useAuth();
  const user = authState.user;
  const { muteConversation, archiveConversation } = useMessaging();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [otherUserName, setOtherUserName] = useState<string | null>(null);

  // Fetch other user's name for direct messages
  useEffect(() => {
    const fetchOtherUserName = async () => {
      if (user && conversation.type === 'direct') {
        const otherUserId = conversation.participants.find((id) => id !== user.uid);
        if (otherUserId) {
          const otherUser = await UserService.getUserById(otherUserId);
          if (otherUser) {
            setOtherUserName(otherUser.displayName || otherUser.email || 'Unknown User');
          }
        }
      }
    };

    fetchOtherUserName();
  }, [conversation, user]);

  if (!user) return null;

  const memberInfo = conversation.memberInfo[user.uid];
  const isMuted = memberInfo?.muted || false;

  // Get conversation display name
  const getConversationName = (): string => {
    if (conversation.type === 'group') {
      return conversation.name || 'Unnamed Group';
    }

    // For direct messages, show the fetched user name or fallback to user ID
    if (otherUserName) {
      return otherUserName;
    }

    const otherUserId = conversation.participants.find((id) => id !== user.uid);
    return otherUserId || 'Unknown User';
  };

  // Get participant count for groups
  const getParticipantText = (): string => {
    if (conversation.type === 'direct') {
      return 'Direct message';
    }
    return `${conversation.participants.length} members`;
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (): void => {
    setAnchorEl(null);
  };

  const handleToggleMute = async (): Promise<void> => {
    try {
      await muteConversation(conversation.id, !isMuted);
      handleMenuClose();
    } catch (error: any) {
      console.error('Error toggling mute:', error);
      alert(`Failed to ${isMuted ? 'unmute' : 'mute'} conversation: ${error.message}`);
    }
  };

  const handleArchive = async (): Promise<void> => {
    if (!window.confirm('Are you sure you want to archive this conversation?')) {
      return;
    }

    try {
      await archiveConversation(conversation.id);
      handleMenuClose();
    } catch (error: any) {
      console.error('Error archiving conversation:', error);
      alert(`Failed to archive conversation: ${error.message}`);
    }
  };

  return (
    <Box
      sx={{
        p: 2,
        borderBottom: 1,
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
      }}
    >
      {/* Back Button (Mobile) */}
      {onBack && (
        <IconButton onClick={onBack} edge="start">
          <BackIcon />
        </IconButton>
      )}

      {/* Avatar */}
      <Avatar>
        {conversation.type === 'group' ? (
          <GroupIcon />
        ) : (
          getConversationName()[0].toUpperCase()
        )}
      </Avatar>

      {/* Conversation Info */}
      <Box sx={{ flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" sx={{ fontSize: '1rem' }}>
            {getConversationName()}
          </Typography>
          {isMuted && <Chip label="Muted" size="small" variant="outlined" />}
        </Box>
        <Typography variant="caption" color="text.secondary">
          {getParticipantText()}
        </Typography>
      </Box>

      {/* Actions Menu */}
      <IconButton onClick={handleMenuOpen}>
        <MoreIcon />
      </IconButton>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleToggleMute}>
          {isMuted ? (
            <>
              <UnmuteIcon sx={{ mr: 1 }} fontSize="small" />
              Unmute
            </>
          ) : (
            <>
              <MuteIcon sx={{ mr: 1 }} fontSize="small" />
              Mute
            </>
          )}
        </MenuItem>
        <MenuItem onClick={handleArchive}>
          <ArchiveIcon sx={{ mr: 1 }} fontSize="small" />
          Archive
        </MenuItem>
        {conversation.type === 'group' && (
          <MenuItem onClick={handleMenuClose}>
            <InfoIcon sx={{ mr: 1 }} fontSize="small" />
            Group Info
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};
